import { SplitAnalysis } from '../types';

export function analyzeSplits(distances: number[], times: number[]): SplitAnalysis {
  if (distances.length !== times.length || distances.length < 4) {
    throw new Error('Invalid split data');
  }
  
  // Calculate velocities for each segment
  const velocities: number[] = [];
  for (let i = 1; i < distances.length; i++) {
    const deltaD = distances[i] - distances[i - 1];
    const deltaT = times[i] - times[i - 1];
    velocities.push(deltaD / deltaT);
  }
  
  // Fit exponential model v(t) = MSS * (1 - e^(-t/TAU))
  // Using non-linear least squares approximation
  const maxV = Math.max(...velocities);
  let bestMSS = maxV * 1.1;
  let bestTAU = 1.0;
  let bestR2 = 0;
  
  // Grid search for best fit
  for (let mss = maxV; mss <= maxV * 1.5; mss += 0.1) {
    for (let tau = 0.5; tau <= 3.0; tau += 0.1) {
      const predictions: number[] = [];
      for (let i = 1; i < times.length; i++) {
        const t = times[i];
        const vPred = mss * (1 - Math.exp(-t / tau));
        predictions.push(vPred);
      }
      
      // Calculate R²
      const meanV = velocities.reduce((a, b) => a + b, 0) / velocities.length;
      const ssRes = velocities.reduce((sum, v, i) => sum + Math.pow(v - predictions[i], 2), 0);
      const ssTot = velocities.reduce((sum, v) => sum + Math.pow(v - meanV, 2), 0);
      const r2 = 1 - (ssRes / ssTot);
      
      if (r2 > bestR2) {
        bestR2 = r2;
        bestMSS = mss;
        bestTAU = tau;
      }
    }
  }
  
  const MAC = bestMSS / bestTAU;
  const t90 = -bestTAU * Math.log(0.1);
  
  return {
    MSS: bestMSS,
    TAU: bestTAU,
    MAC,
    t90,
    R2: bestR2
  };
}

export function calculateAccelerations(distances: number[], times: number[]): number[] {
  const accelerations: number[] = [];
  
  for (let i = 2; i < distances.length; i++) {
    const v1 = (distances[i - 1] - distances[i - 2]) / (times[i - 1] - times[i - 2]);
    const v2 = (distances[i] - distances[i - 1]) / (times[i] - times[i - 1]);
    const dt = times[i] - times[i - 1];
    accelerations.push((v2 - v1) / dt);
  }
  
  return accelerations;
}